/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from "react";
import {
  ContextualSaveBar,
  Page,
  Card,
  Badge,
  Select,
  FormLayout,
  TextField,
  SkeletonBodyText,
  SkeletonDisplayText,
  Loading,
} from "@medentic/polaris-react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Member, Role } from "@shared/types/settings";
import { EntityType } from "@shared/types/commonTypes";
import { getBadgeTone } from "@shared/lib";
import { ROOTS } from "@app/config/constants";
import useFormattedDate from "@shared/hooks/common/useFormattedDate";
import usePageMeta from "@shared/hooks/common/usePageMeta";

import {
  PersonRemoveIcon,
  StatusActiveIcon,
  XCircleIcon,
} from "@medentic/polaris-icons";

const EditMember = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{
    clinicId?: string;
    eventOrganizerId?: string;
    memberId: string;
  }>();
  const companyId = params.clinicId || params.eventOrganizerId!;
  const memberId = params.memberId!;
  const entityType = params.clinicId ? EntityType.CLINIC : EntityType.EVENT;
  const basePath = entityType === EntityType.CLINIC ? ROOTS.CLINIC : ROOTS.EVENTS;
  const queryClient = useQueryClient();
  const { GET, PATCH, DELETE } = useApiRequest();

  const [selectedRole, setSelectedRole] = useState<string>("");
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const {
    data: memberData,
    isLoading: isLoadingMember,
    isFetching: isFetchingMember,
  } = useQuery<Member>({
    queryKey: ["member", companyId, memberId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${companyId}/team/members/${memberId}`),
  });

  const { data: rolesData = [], isLoading: isLoadingRoles } = useQuery<Role[]>({
    queryKey: ["roles", companyId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${companyId}/team/roles-and-permissions`),
  });

  useEffect(() => {
    if (memberData?.userPermissions?.roleId) {
      setSelectedRole(memberData.userPermissions.roleId.toString());
    }
  }, [memberData]);

  // Prepare page meta data
  const memberName = memberData ? `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim() : undefined;
  const memberAvatar = memberData?.avatar?.image;

  // Update page meta when member data is available
  usePageMeta(memberName, memberAvatar);

  const updateRoleMutation = useMutation({
    mutationFn: (roleId: string) =>
      PATCH(`/api/healthcare-companies/${companyId}/team/members/${memberId}`, {
        roleId: parseInt(roleId, 10),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["member", companyId, memberId],
      });
      queryClient.invalidateQueries({
        queryKey: ["teamMembers", companyId],
      });
      setIsDirty(false);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: number) =>
      PATCH(`/api/healthcare-companies/${companyId}/team/members/${memberId}`, {
        status,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["teamMembers", companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["member", companyId, memberId],
      });
    },
  });

  const deleteMemberMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${companyId}/team/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", companyId] });
      navigate(`${basePath}/${companyId}/settings/members-permissions`);
    },
  });

  const handleRoleChange = useCallback((value: string) => {
    setSelectedRole(value);
    setIsDirty(true);
  }, []);

  const handleActivateDeactivate = useCallback(() => {
    const newStatus = memberData?.status === 1 ? 0 : 1;
    updateStatusMutation.mutate(newStatus);
  }, [memberData, updateStatusMutation]);

  const handleSave = useCallback(() => {
    updateRoleMutation.mutate(selectedRole);
  }, [selectedRole, updateRoleMutation]);

  const handleRemove = useCallback(() => {
    deleteMemberMutation.mutate();
  }, [deleteMemberMutation]);

  const invitedBy =
    memberData?.inviter?.firstName || memberData?.inviter?.lastName
      ? `${memberData?.inviter?.firstName} ${memberData?.inviter?.lastName}`
      : undefined;
  const { formattedDate: invitedOn } = useFormattedDate({
    date: memberData?.createdAt ?? "",
  });
  const roleOptions = rolesData.map((role) => ({
    label: role.name,
    value: role.id.toString(),
  }));

  return (
    <>
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            onAction: handleSave,
            loading: updateRoleMutation.isPending,
          }}
          discardAction={{
            onAction: () => setIsDirty(false),
          }}
        />
      )}
      {isFetchingMember && <Loading />}
      {isLoadingMember || isLoadingRoles ? (
        <Page>
          <Loading />
          <SkeletonDisplayText />
          <br />
          <Card>
            <SkeletonBodyText lines={7} />
          </Card>
        </Page>
      ) : (
        <Page
          backAction={{
            onAction: () =>
              navigate(`${basePath}/${companyId}/settings/members-permissions`),
          }}
          title={`${memberData?.firstName} ${memberData?.lastName}`}
          titleMetadata={
            <Badge
              tone={getBadgeTone(
                memberData?.status === 1 ? "active" : "inactive"
              )}
            >
              {t(memberData?.status === 1 ? "common.active" : "common.inactive")}
            </Badge>
          }
          subtitle={
            invitedBy
              ? t("membersAndPermissions.invitedBy", {
                  name: invitedBy,
                  date: invitedOn,
                })
              : t("membersAndPermissions.invitedOn", {
                  date: invitedOn,
                })
          }
          secondaryActions={[
            {
              content:
                memberData?.status === 1
                  ? t("common.deactivate")
                  : t("common.activate"),
              onAction: handleActivateDeactivate,
              loading: updateStatusMutation.isPending,
              icon: memberData?.status === 1 ? XCircleIcon : StatusActiveIcon,
            },
            {
              content: t("common.remove"),
              onAction: handleRemove,
              loading: deleteMemberMutation.isPending,
              destructive: true,
              icon: PersonRemoveIcon,
            },
          ]}
        >
          <Card>
            <FormLayout>
              <TextField
                label={t("common.email")}
                value={memberData?.email ?? ""}
                disabled
                autoComplete="off"
              />
              <Select
                label={t("common.role")}
                options={roleOptions}
                value={selectedRole}
                onChange={handleRoleChange}
                disabled={updateRoleMutation.isPending}
                helpText={t("membersAndPermissions.roleFooter")}
              />
            </FormLayout>
          </Card>
        </Page>
      )}
    </>
  );
};

export default EditMember;
